import {
  ANALYTICS_COMMON_PATTERNS,
  ANALYTICS_EVENTS,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  type AnalyticsPropertySpec,
  type AnalyticsScreen,
} from "./contract/analytics";
import { osVersionFromUserAgent } from "../lib/os.js";

export type { AnalyticsEventName, AnalyticsEventProperties } from "./contract/analytics";

// The coarse OS naming shared with the `X-MeetFolks-Build` header (#980).
export { osVersionFromUserAgent };

const PREFERENCE_KEY = "meetfolks-share-anonymous-usage-stats";

export type AnalyticsScreenName = AnalyticsScreen;
export type ScreenName = AnalyticsScreenName;

/** The properties every event carries on every platform (`common` in contract/analytics.json). */
export type CommonProperties = {
  platform: "web";
  app_version: string;
  os_version: string;
};

type CaptureResult = {
  event: string;
  properties: Record<string, unknown>;
  [key: string]: unknown;
};

type BeforeSend = (event: CaptureResult | null) => CaptureResult | null;

type PostHogOptions = {
  api_host: string;
  ip: false;
  person_profiles: "never";
  disable_external_dependency_loading: true;
  disable_session_recording: true;
  capture_pageview: false;
  capture_pageleave: false;
  autocapture: false;
  persistence: "localStorage";
  disable_surveys: true;
  advanced_disable_flags: true;
  capture_exceptions: false;
  request_batching: false;
  before_send: BeforeSend;
};

type PostHogClient = {
  init(apiKey: string, options: PostHogOptions): PostHogClient;
  capture(event: AnalyticsEventName, properties: Record<string, unknown>): void;
};

type AnalyticsControllerOptions = {
  apiKey?: string;
  loadSdk: () => Promise<PostHogClient>;
  apiHost: () => string;
  getGlobalPrivacyControl: () => boolean;
  storage: Pick<Storage, "getItem" | "setItem">;
  common: () => CommonProperties;
};

/** Whether `value` is one the shared event list allows for a property. */
function allowedValue(spec: AnalyticsPropertySpec, value: unknown): boolean {
  if ("enum" in spec) return typeof value === "string" && spec.enum.includes(value);
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= spec.integer.minimum &&
    value <= spec.integer.maximum
  );
}

/** The common properties, each held to its pattern; a value that misses it is `unknown`/`other`. */
function checkedCommon(common: CommonProperties): CommonProperties {
  return {
    platform: "web",
    app_version: ANALYTICS_COMMON_PATTERNS.app_version.test(common.app_version)
      ? common.app_version
      : "unknown",
    os_version: ANALYTICS_COMMON_PATTERNS.os_version.test(common.os_version)
      ? common.os_version
      : "other",
  };
}

export function screenNameForPath(pathname: string): ScreenName | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  // Dev-only surfaces report nothing. `/app/signin` is the native return's redirect alias:
  // it emits nothing of its own, and the `signin` it lands on is the one screen view (#1085).
  if (path === "/kitchen-sink" || path === "/parity" || path === "/diagnostics") return null;
  if (path === "/app/signin") return null;
  if (path === "/") return "home";
  if (path === "/session") return "session";
  if (/^\/session\/[^/]+\/result$/.test(path)) return "result";

  const fixed: Record<string, ScreenName> = {
    "/welcome": "welcome",
    "/enroll": "enroll",
    "/rerecord": "rerecord",
    "/signin": "signin",
    "/consent": "consent",
    "/sessions": "sessions",
    "/account": "account",
    "/plans": "plans",
    "/credits": "credits_history",
    "/settings": "settings",
    "/delete-account": "delete_account",
    "/unsubscribe": "email_unsubscribe",
  };
  // The wildcard route is `not_found`; no path is `other` on the web any more.
  return fixed[path] ?? "not_found";
}

/**
 * Keep the PostHog SDK's event body on a small allowlist. In particular, its automatic
 * URL, referrer, initial URL, and screen-size properties are removed before transport.
 * The event's own properties and their values come from the shared list,
 * `contract/analytics.json`; the common properties are added last, when given.
 */
export function sanitizePostHogEvent(
  event: CaptureResult | null,
  projectKey?: string,
  common?: CommonProperties,
): CaptureResult | null {
  if (!event || !Object.prototype.hasOwnProperty.call(ANALYTICS_EVENTS, event.event)) return null;
  const own = ANALYTICS_EVENTS[event.event as AnalyticsEventName];

  const allowed = new Set([
    "$lib",
    "$lib_version",
    "$process_person_profile",
    "$session_id",
    "$window_id",
    "distinct_id",
    "token",
    ...Object.keys(own),
  ]);
  const properties: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(event.properties ?? {})) {
    if (!allowed.has(key)) continue;
    if (key === "$lib" || key === "$lib_version") {
      if (typeof value === "string" && value.length <= 64) properties[key] = value;
      continue;
    }
    if (key === "$process_person_profile") {
      if (value === false) properties[key] = false;
      continue;
    }
    if (key === "token") {
      // PostHog's ingest endpoint requires its public project key in each event.
      // Preserve only this exact SDK key, never a different app or account token.
      if (projectKey && value === projectKey) properties[key] = value;
      continue;
    }
    if (key === "distinct_id" || key === "$session_id" || key === "$window_id") {
      // The SDK's UUIDs. With localStorage persistence the `distinct_id` is this
      // browser install's own, kept across visits: random, never an account id,
      // and gone when the person clears the site's storage.
      if (typeof value === "string" && /^[0-9a-f-]{32,36}$/i.test(value)) properties[key] = value;
      continue;
    }
    const spec = own[key];
    if (spec && allowedValue(spec, value)) properties[key] = value;
  }
  if (common) Object.assign(properties, checkedCommon(common));

  const safe: CaptureResult = { event: event.event, properties };
  for (const key of ["uuid", "timestamp"] as const) {
    if (event[key] !== undefined) safe[key] = event[key];
  }
  return safe;
}

export function createAnalyticsController(options: AnalyticsControllerOptions) {
  let preference: boolean | null = null;
  let client: PostHogClient | null = null;
  let loading: Promise<void> | null = null;
  let queued: Array<{ event: AnalyticsEventName; properties: Record<string, unknown> }> = [];
  let lastScreenNavigationKey: string | null = null;

  // Off until the person turns it on in Settings (#1250, App Review 5.1.1(ii)): only a stored
  // "on" shares. A stored "off", nothing stored, or storage that cannot be read all mean off.
  function readPreference(): boolean {
    if (preference !== null) return preference;
    try {
      preference = options.storage.getItem(PREFERENCE_KEY) === "on";
    } catch {
      preference = false;
    }
    return preference;
  }

  function isEnabled(): boolean {
    return Boolean(options.apiKey?.trim()) && readPreference() && !options.getGlobalPrivacyControl();
  }

  /**
   * Whether the one-time ask may show (#1259): only while no choice is stored at all, so
   * anyone who used the Settings switch or answered (or ignored) the ask is never asked,
   * and only where sharing could happen (a configured key, no Global Privacy Control).
   * Storage that cannot be read cannot remember an answer either, so it is never asked.
   */
  function canAsk(): boolean {
    if (!options.apiKey?.trim() || options.getGlobalPrivacyControl()) return false;
    try {
      return options.storage.getItem(PREFERENCE_KEY) === null;
    } catch {
      return false;
    }
  }

  function send(event: AnalyticsEventName, properties: Record<string, unknown>) {
    if (!client) return;
    try {
      client.capture(event, properties);
    } catch {
      // A broken optional SDK must not interrupt recording, enrollment or joining.
    }
  }

  function capture<K extends AnalyticsEventName>(event: K, properties: AnalyticsEventProperties[K]) {
    if (!isEnabled()) return;
    const safeProperties = properties as Record<string, unknown>;
    if (client) {
      send(event, safeProperties);
    } else if (queued.length < 30) {
      queued.push({ event, properties: safeProperties });
    }
  }

  function start() {
    if (!isEnabled() || client || loading) return loading ?? Promise.resolve();

    loading = options.loadSdk()
      .then((sdk) => {
        if (!isEnabled()) {
          queued = [];
          return;
        }
        const apiKey = options.apiKey!.trim();
        const initialized = sdk.init(apiKey, {
          api_host: options.apiHost(),
          ip: false,
          person_profiles: "never",
          disable_external_dependency_loading: true,
          disable_session_recording: true,
          capture_pageview: false,
          capture_pageleave: false,
          autocapture: false,
          // The install's own id: localStorage keeps the `distinct_id` across visits, so
          // one browser's events read as one and updates never reset it -- while it is never
          // an account id, and clearing the site's storage clears it.
          persistence: "localStorage",
          disable_surveys: true,
          advanced_disable_flags: true,
          capture_exceptions: false,
          // One request per event, sent as it is captured. `before_send` runs at
          // capture time, so with batching an event captured just before the user
          // turned sharing off was still posted with the batch afterwards.
          request_batching: false,
          before_send: (event) => {
            if (!isEnabled()) return null;
            const safe = sanitizePostHogEvent(event, apiKey, options.common());
            if (safe) {
              // PostHog sees the Worker connection, not the visitor's IP. Add this
              // after the allowlist so its GeoIP transform cannot label visitors
              // with the proxy's location.
              safe.properties.$geoip_disable = true;
            }
            return safe;
          },
        });
        client = initialized;
        const waiting = queued;
        queued = [];
        for (const item of waiting) {
          if (!isEnabled()) break;
          send(item.event, item.properties);
        }
      })
      .catch(() => {
        // Analytics is optional. A blocked edge, failed chunk or SDK error must not
        // affect app boot or the user's recording flow.
        queued = [];
      })
      .finally(() => {
        loading = null;
      });
    return loading;
  }

  function setEnabled(enabled: boolean) {
    preference = enabled;
    try {
      options.storage.setItem(PREFERENCE_KEY, enabled ? "on" : "off");
    } catch {
      // This browser can still apply the choice for the current page.
    }
    if (!enabled) {
      queued = [];
      return;
    }
    void start();
  }

  function captureScreenViewed(pathname: string, navigationKey: string) {
    if (navigationKey === lastScreenNavigationKey) return;
    lastScreenNavigationKey = navigationKey;
    const screen = screenNameForPath(pathname);
    if (screen) capture("screen_viewed", { screen });
  }

  return { canAsk, capture, captureScreenViewed, isEnabled, readPreference, setEnabled, start };
}

const controller = createAnalyticsController({
  apiKey: import.meta.env.VITE_POSTHOG_KEY,
  loadSdk: async () => {
    const { default: posthog } = await import("posthog-js");
    return posthog as unknown as PostHogClient;
  },
  apiHost: () => `${window.location.origin}/ingest`,
  getGlobalPrivacyControl: () =>
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true,
  storage: {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
  },
  common: () => ({
    platform: "web",
    app_version: (globalThis as { __MEETFOLKS_BUILD__?: string }).__MEETFOLKS_BUILD__ ?? "dev",
    os_version: osVersionFromUserAgent(typeof navigator !== "undefined" ? navigator.userAgent : ""),
  }),
});

export const captureAnalytics = controller.capture;
export const startAnalytics = controller.start;
export const captureScreenViewed = controller.captureScreenViewed;
export const getShareUsageStatsPreference = controller.readPreference;
export const isAnalyticsEnabled = controller.isEnabled;
export const setShareUsageStatsEnabled = controller.setEnabled;
export const canAskUsageStats = controller.canAsk;
export function isGlobalPrivacyControlEnabled(): boolean {
  return typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}
